import React from "react";
import { NavLink, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Toaster } from "../components/ui/sonner";
import {
  Gauge,
  ClipboardText,
  GraduationCap,
  BookOpen,
  SignOut,
  User,
  Gear,
  Receipt,
  UsersThree,
  Buildings,
  Briefcase,
} from "@phosphor-icons/react";

const navItems = [
  { to: "/admin", label: "Overview", icon: Gauge, end: true, testid: "nav-overview" },
  { to: "/admin/pending", label: "Pending Approvals", icon: ClipboardText, testid: "nav-pending" },
  { to: "/admin/approved", label: "Approved & Enrolled", icon: GraduationCap, testid: "nav-approved" },
  { to: "/admin/courses", label: "Courses", icon: BookOpen, testid: "nav-courses" },
  { to: "/admin/placements", label: "Placements", icon: Briefcase, testid: "nav-placements" },
  { to: "/admin/companies", label: "Partner Companies", icon: Buildings, testid: "nav-companies" },
  { to: "/admin/receipts", label: "Receipts Ledger", icon: Receipt, testid: "nav-receipts" },
  { to: "/admin/users", label: "Users", icon: UsersThree, testid: "nav-users", adminOnly: true },
  { to: "/admin/settings", label: "Settings", icon: Gear, testid: "nav-settings", adminOnly: true },
];

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 flex">
      {/* Sidebar */}
      <aside className="w-64 border-r border-slate-200 flex flex-col bg-slate-50/50 sticky top-0 h-screen">
        <div className="px-6 py-6 border-b border-slate-200">
          <Link to="/admin" className="block" data-testid="brand-link">
            <div className="font-display text-xl font-black tracking-tighter">
              SMS<span className="text-[var(--sms-primary)]">.</span>
            </div>
            <div className="font-mono-sms text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-1">
              Student Mgmt System
            </div>
          </Link>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {navItems.map((item) => {
            if (item.adminOnly && user?.role !== "admin") return null;
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 text-sm font-medium border-l-2 transition-colors ${
                    isActive
                      ? "bg-white text-[var(--sms-primary)] border-[var(--sms-primary)]"
                      : "text-slate-600 border-transparent hover:bg-white hover:text-slate-900"
                  }`
                }
              >
                <Icon size={18} weight="duotone" />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="border-t border-slate-200 p-3">
          <div className="flex items-center gap-3 px-2 py-2 mb-2">
            <div className="w-8 h-8 bg-slate-900 text-white flex items-center justify-center">
              <User size={16} weight="bold" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate" data-testid="user-name">
                {user?.name || "Administrator"}
              </div>
              <div className="text-[10px] font-mono-sms text-slate-500 truncate uppercase" data-testid="user-role">
                {user?.role || "admin"} · {user?.email}
              </div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-600 hover:text-[var(--sms-danger)] border border-slate-200 hover:border-[var(--sms-danger)] transition-colors"
          >
            <SignOut size={14} weight="bold" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <div className="max-w-7xl mx-auto px-8 py-8">{children}</div>
      </main>

      <Toaster position="top-right" richColors />
    </div>
  );
}
